<template>
  <div>
    <Head :title="clientDisplayName" />

    <!-- Header con información del cliente -->
    <div class="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
      <div>
        <h1 class="text-2xl font-bold mb-1">
          {{ clientDisplayName }}
        </h1>
        <p class="text-gray-600">RIF: {{ client.identification }}</p>
      </div>
      
      <div class="flex gap-3">
        <a :href="`/reports/clients/${client.id}`" 
           target="_blank"
           class="btn-blue flex items-center gap-2">
          <i class="fa fa-print"></i>
          Imprimir
        </a>
      </div>
    </div>

    <div class="grid gap-6 grid-cols-1 md:grid-cols-2">
      <!-- Panel de Pagos -->
      <section class="bg-white border border-slate-200 rounded-xl shadow-sm p-6">
        <div class="flex justify-between items-center mb-4">
          <h2 class="text-xl font-bold">Registrar pagos</h2>
          <div class="text-sm text-gray-600">
            Total pagos: ${{ formatNumber(totalPayments) }}
          </div>
        </div>
        
        <payment-box-general 
          :payment_methods="payment_methods" 
          :client="client"
          @payment-added="refreshPayments"
          class="mb-6"
        />

        <div class="overflow-x-auto">
          <table class="w-full">
            <thead>
              <tr class="text-left bg-gray-50">
                <th class="p-4 font-semibold">Numero</th>
                <th class="p-4 font-semibold">Metodo</th>
                <th class="p-4 font-semibold">Monto</th>
                <th class="p-4 font-semibold">Fecha</th>
                <th class="p-4 font-semibold">Responsable</th>
                <th class="p-4 font-semibold">Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="pay in sortedPayments" 
                  :key="pay.id" 
                  class="hover:bg-gray-50 border-t border-gray-100">
                <td class="p-4">{{ pay.number }}</td>
                <td class="p-4 capitalize">
                  <span class="inline-flex items-center gap-2">
                    <i :class="getPaymentMethodIcon(pay.method)" class="text-gray-500"></i>
                    {{ pay.method }}
                  </span>
                </td>
                <td class="p-4 font-medium">${{ formatNumber(pay.amount) }}</td>
                <td class="p-4">{{ formatDate(pay.created_at) }}</td>
                <td class="p-4">{{ getFullName(pay.user) }}</td>
                <td class="p-4">
                  <div class="flex gap-3">
                    <Link :href="`/payments/${pay.id}/receipt`" 
                          class="text-blue-600 hover:text-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded p-1"
                          title="Imprimir recibo">
                      <i class="fa fa-print"></i>
                    </Link>
                    <button v-if="canVoidPayment(pay)"
                            @click="voidPayment(pay.id)"
                            class="text-red-600 hover:text-red-800 focus:outline-none focus:ring-2 focus:ring-red-500 rounded p-1"
                            title="Anular pago">
                      <i class="fa fa-times-circle"></i>
                    </button>
                  </div>
                </td>
              </tr>
              <tr v-if="!payments.length" class="border-t border-gray-100">
                <td colspan="6" class="p-6 text-center text-gray-500">
                  No hay pagos registrados
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <!-- Panel de Saldo y Órdenes -->
      <section class="space-y-6">
        <div class="grid grid-cols-2 gap-4">
          <box-dashboard title="Saldo Pendiente" class="bg-blue-50">
            <p class="text-center text-3xl font-black" :class="balanceColorClass">
              ${{ formatNumber(client.balance) }}
            </p>
          </box-dashboard>

          <box-dashboard title="Límite de Crédito" class="bg-gray-50">
            <p class="text-center text-3xl font-black text-gray-700">
              ${{ formatNumber(client.credit_limit || 0) }}
            </p>
          </box-dashboard>
        </div>

        <div class="bg-white rounded-xl shadow overflow-hidden">
          <div class="p-4 bg-gray-50 border-b border-gray-200">
            <h3 class="font-bold text-gray-700">Órdenes Pendientes</h3>
          </div>
          <div class="overflow-x-auto">
            <table class="w-full min-w-[500px]">
              <thead>
                <tr class="text-left bg-gray-50">
                  <th class="p-4 font-semibold text-gray-700">Orden #</th>
                  <th class="p-4 font-semibold text-gray-700">Estado</th>
                  <th class="p-4 font-semibold text-gray-700">Total</th>
                  <th class="p-4 font-semibold text-gray-700">Balance</th>
                  <th class="p-4 font-semibold text-gray-700">Vencimiento</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="order in sortedOrders" 
                    :key="order.id"
                    class="hover:bg-gray-50 border-t border-gray-100 transition-colors duration-150">
                  <td class="p-4">
                    <Link :href="`/orders/${order.id}`" 
                          class="text-blue-600 hover:text-blue-800 font-medium hover:underline focus:outline-none focus:ring-2 focus:ring-blue-500 rounded">
                      {{ order.number_order }}
                    </Link>
                  </td>
                  <td class="p-4">
                    <span :class="[
                      getOrderStateClass(order.order_state),
                      'px-3 py-1 rounded-full text-sm font-medium capitalize'
                    ]">
                      {{ order.order_state }}
                    </span>
                  </td>
                  <td class="p-4 font-medium">${{ formatNumber(order.total) }}</td>
                  <td class="p-4 font-medium">${{ formatNumber(order.balance) }}</td>
                  <td class="p-4">
                    <span :class="getDueDateClass(order.due_date)">
                      {{ formatDate(order.due_date) }}
                    </span>
                  </td>
                </tr>
                <tr v-if="!orders.length" class="border-t border-gray-100">
                  <td colspan="5" class="p-6 text-center text-gray-500">
                    No hay órdenes pendientes
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '../../Shared/Layout.vue'
import BoxDashboard from '@/Shared/BoxDashboard.vue'
import PaymentBoxGeneral from '../../Shared/PaymentBoxGeneral.vue'
import { Inertia } from '@inertiajs/inertia'

export default {
  name: 'PaymentsShow',
  
  components: {
    Head,
    Link,
    BoxDashboard,
    PaymentBoxGeneral,
  },

  layout: Layout,

  props: {
    client: {
      type: Object,
      required: true
    },
    orders: {
      type: Object,
      required: true
    },
    payment_methods: {
      type: Object,
      required: true
    },
    payments: {
      type: Object,
      required: true
    }
  },

  computed: {
    clientDisplayName() {
      return this.client?.companyName ?? this.client?.name
    },

    totalPayments() {
      return this.payments.reduce((sum, payment) => sum + payment.amount, 0)
    },

    sortedPayments() {
      return [...this.payments].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    },

    sortedOrders() {
      return [...this.orders].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    },

    balanceColorClass() {
      return this.client.balance > 0 ? 'text-red-600' : 'text-green-600'
    }
  },

  methods: {
    formatNumber(value) {
      return value.toLocaleString('es', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    },

    formatDate(date) {
      return new Date(date).toLocaleDateString('es', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    },

    getFullName(user) {
      return `${user.first_name} ${user.last_name}`
    },

    getOrderStateClass(state) {
      const classes = {
        'pending': 'bg-yellow-100 text-yellow-800',
        'completed': 'bg-green-100 text-green-800',
        'cancelled': 'bg-red-100 text-red-800'
      }
      return classes[state] || 'bg-gray-100 text-gray-800'
    },

    getPaymentMethodIcon(method) {
      const icons = {
        'efectivo': 'fa fa-money',
        'transferencia': 'fa fa-bank',
        'tarjeta': 'fa fa-credit-card',
        'cheque': 'fa fa-money-check'
      }
      return icons[method.toLowerCase()] || 'fa fa-circle'
    },

    getDueDateClass(date) {
      const dueDate = new Date(date)
      const today = new Date()
      return dueDate < today ? 'text-red-600 font-medium' : 'text-gray-600'
    },

    canVoidPayment(payment) {
      const paymentDate = new Date(payment.created_at)
      const today = new Date()
      return paymentDate.toDateString() === today.toDateString()
    },

    async voidPayment(paymentId) {
      if (!confirm('¿Está seguro de anular este pago?')) return

      try {
        await Inertia.delete(`/payments/${paymentId}`)
        this.$toast.success('Pago anulado correctamente')
      } catch (error) {
        this.$toast.error('Error al anular el pago')
      }
    },

    refreshPayments() {
      Inertia.reload({ only: ['payments', 'client'] })
    }
  }
}
</script>
